import { Component, inject, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { map, take, takeUntil } from 'rxjs/operators';
import { ClientNotesModalComponent } from '../client-notes-modal/client-notes-modal.component';
import { InstanceTypes } from '../../models/instance-types';
import { Select, Store } from '@ngxs/store';
import { ClientSelectionState } from '../../state/ClientSelectionState/ClientSelection.state';
import { forkJoin, Observable, Subject } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { ClientSetup } from '../../models/client';
import { EmployerSelectionState } from '../../state/EmployerSelectionState/EmployerSelection.state';
import { CompaniesService } from '../../services/companies.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PayrollManagement } from '../../tools/payroll-management';
import { ActivatedRoute } from '@angular/router';
import { AclService } from '../../services/acl.service';
import { RouteParamsService } from '../../services/route-params.service';
import { SwipeclockService } from '../../services/swipeclock.service';
import { ClientFeatureWhitelistService } from '../../services/client-feature-whitelist.service';
import { ClientTimeOffRequestService } from '../../services/time-off-request.service';

@Component({
  selector: 'lib-client-nav-list',
  templateUrl: './client-nav-list.component.html',
  styleUrl: './client-nav-list.component.scss',
  standalone: false
})
export class ClientNavListComponent implements OnInit, OnDestroy {
  @Input() sideView = true;
  @Input() activeTab = 'Home';
  clientCode: Observable<string> = inject(Store).select(ClientSelectionState.clientCode);
  baseUrl: string;
  payrollRoute: string;
  canViewReports = false;
  canEditPeople = false;
  canDoPayroll = false;
  clientSetup: Observable<ClientSetup> = inject(Store).select(ClientSelectionState.clientSetup);
  swipeclockJWTSucceeded: boolean = false;
  private destroy$ = new Subject<void>();
  canShowTimeOff: Observable<boolean> = inject(Store).select(ClientSelectionState.canEnableTimeOff);
  hasTimeOffPermission = false;

  public linkClasses = {
    btn: !this.sideView,
    'btn-link': !this.sideView,
    'nav-link': !this.sideView
  };

  get isEmployeePortal() {
    return this.env.instanceType === InstanceTypes.EmployeePortal;
  }
  get isClientPortal() {
    return this.env.instanceType === InstanceTypes.ClientPortal;
  }
  get hasSwipeclockAccess() {
    return this.hasSwipeclockClientSetup.pipe(
      take(1),
      map(result => {
        return result && this.swipeclockJWTSucceeded;
      })
    );
  }
  get hasSwipeclockClientSetup() {
    return this.clientSetup.pipe(
      take(1),
      map(clientSetup => {
        return (
          clientSetup != null &&
          clientSetup.TimeclockVendorId == 'SC' &&
          (clientSetup.AllowTimeclockPunch ||
            clientSetup.AllowTimeclockPTO ||
            clientSetup.AllowTimeclockSchedule)
        );
      })
    );
  }

  constructor(
    public authService: AuthService,
    private companyService: CompaniesService,
    private ngModalService: NgbModal,
    private payrollMgmt: PayrollManagement,
    private route: ActivatedRoute,
    private aclService: AclService,
    private routeParamsService: RouteParamsService,
    private swipeclockSvc: SwipeclockService,
    private store: Store,
    private clientFeatureWhitelistService: ClientFeatureWhitelistService,
    private clientTimeOffRequestService: ClientTimeOffRequestService,
    @Inject('env') private env: any
  ) {}

  ngOnInit() {
    if (!this.isEmployeePortal) {
      this.baseUrl = this.env.instanceType === InstanceTypes.ClientPortal ? '' : '/client';
      this.clientCode.pipe(takeUntil(this.destroy$)).subscribe(clientCode => {
        this.payrollRoute = `/client/${clientCode}/payroll-landing`;
        if (this.env.instanceType === InstanceTypes.ClientPortal && clientCode != null) {
          const userId = this.authService.user.UserId;
          this.aclService
            .AccessCheck(userId, 'Client Portal Admin', 'Reports', clientCode)
            .subscribe(result => {
              this.canViewReports = result;
            });
          this.aclService
            .AccessCheck(userId, 'Client Portal Admin', 'EditPeople', clientCode)
            .subscribe(result => {
              this.canEditPeople = result;
            });
          this.aclService
            .AccessCheck(userId, 'Client Portal Admin', 'RunPayroll', clientCode)
            .subscribe(result => {
              this.canDoPayroll = result;
            });
          this.aclService
            .AccessCheck(userId, 'Client Portal Admin', 'timeOff', clientCode)
            .subscribe(result => {
              this.hasTimeOffPermission = result;
            });
        } else if (this.env.instanceType === InstanceTypes.CsrPortal) {
          this.canViewReports = true;
          this.canEditPeople = true;
          this.canDoPayroll = true;
          this.hasTimeOffPermission = true;
        }
      });
    } else {
      this.clientCode.pipe(takeUntil(this.destroy$)).subscribe(clientCode => {
        if (clientCode != null) {
          const obs: Observable<any>[] = [
            this.hasSwipeclockClientSetup,
            this.store.selectOnce(EmployerSelectionState.personId)
          ];
          forkJoin(obs).subscribe(result => {
            if (result[0]) {
              const personId = result[1];
              this.swipeclockSvc
                .getPortalJwt('EmployeeSSO', clientCode, personId.toString())
                .subscribe(ret => {
                  this.swipeclockJWTSucceeded = ret.success;
                });
            }
          });
        }
      });
    }
  }
  public ngOnDestroy() {
    this.destroy$.next();
  }

  public openClientNotesModal() {
    this.clientCode.pipe(take(1)).subscribe(clientCode => {
      const modalRef = this.ngModalService.open(ClientNotesModalComponent, {
        size: 'lg',
        windowClass: 'centered'
      });
      modalRef.componentInstance.clientCode = clientCode;
    });
  }
}
